import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Pencil, Trash2, Calendar, User, Clock, X, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import type { WebsiteTask, WebsiteName, TaskStatus, TaskPriority } from '@/types/website';
import { 
  WEBSITE_LABELS, 
  TASK_STATUS_LABELS, 
  TASK_PRIORITY_LABELS 
} from '@/types/website';
import { PageTagSelector } from '@/components/website/PageTagSelector';
import { useProfiles } from '@/hooks/useProfiles';

const priorityColors: Record<string, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const statusColors: Record<string, string> = {
  idee: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  in_uitvoering: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  voltooid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

export default function CRMWebsiteTaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profiles } = useProfiles();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWebsite, setEditWebsite] = useState<WebsiteName>('greenvision');
  const [editStatus, setEditStatus] = useState<TaskStatus>('idee');
  const [editPriority, setEditPriority] = useState<TaskPriority>('medium');
  const [editAssignedTo, setEditAssignedTo] = useState<string>('none');
  const [editDueDate, setEditDueDate] = useState('');
  const [editLabels, setEditLabels] = useState<string[]>([]);
  const [editPageTags, setEditPageTags] = useState<string[]>([]);
  const [newLabel, setNewLabel] = useState('');

  const { data: task, isLoading } = useQuery({
    queryKey: ['website-task', taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_tasks')
        .select(`
          *,
          assigned_profile:profiles!website_tasks_assigned_to_fkey(display_name, email),
          created_profile:profiles!website_tasks_created_by_fkey(display_name, email)
        `)
        .eq('id', taskId)
        .single();

      if (error) throw error;
      return data as WebsiteTask;
    },
    enabled: !!taskId,
  });

  useEffect(() => {
    if (task) {
      setEditTitle(task.title);
      setEditDescription(task.description || '');
      setEditWebsite(task.website);
      setEditStatus(task.status);
      setEditPriority(task.priority);
      setEditAssignedTo(task.assigned_to || 'none');
      setEditDueDate(task.due_date || '');
      setEditLabels(task.labels || []);
      setEditPageTags(task.page_tags || []);
    }
  }, [task]);

  const handleSave = async () => {
    if (!taskId) return;

    const updates: Partial<WebsiteTask> = {
      title: editTitle,
      description: editDescription || null,
      website: editWebsite,
      status: editStatus,
      priority: editPriority,
      assigned_to: editAssignedTo === 'none' ? null : editAssignedTo,
      due_date: editDueDate || null,
      labels: editLabels,
      page_tags: editPageTags,
      completed_at: editStatus === 'voltooid' && task?.status !== 'voltooid' 
        ? new Date().toISOString() 
        : task?.completed_at || null,
    };

    const { error } = await supabase
      .from('website_tasks')
      .update(updates)
      .eq('id', taskId);

    if (error) {
      toast.error('Fout bij opslaan');
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['website-task', taskId] });
    queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['website-page-tags'] });
    toast.success('Taak opgeslagen');
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!taskId) return;

    const { error } = await supabase
      .from('website_tasks')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', taskId);

    if (error) {
      toast.error('Fout bij verwijderen');
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
    toast.success('Taak verwijderd');
    navigate('/crm/website/taken');
  };

  const handlePageTagsChange = async (tags: string[]) => {
    setEditPageTags(tags);
    if (!isEditing && taskId) {
      const { error } = await supabase
        .from('website_tasks')
        .update({ page_tags: tags })
        .eq('id', taskId);

      if (error) {
        toast.error('Fout bij opslaan tags');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['website-task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['website-page-tags'] });
    }
  };

  const addLabel = () => {
    if (newLabel.trim() && !editLabels.includes(newLabel.trim())) {
      setEditLabels([...editLabels, newLabel.trim()]);
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setEditLabels(editLabels.filter(l => l !== label));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">Taak niet gevonden</div>
        <Button variant="outline" onClick={() => navigate('/crm/website/taken')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug naar taken
        </Button>
      </div>
    );
  }

  const assignee = task.assigned_profile;
  const creator = task.created_profile;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/crm/website/taken')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug naar taken
        </Button>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Annuleren
              </Button>
              <Button onClick={handleSave}>Opslaan</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Bewerken
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Verwijderen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Taak verwijderen?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Weet je zeker dat je deze taak wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuleren</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>Verwijderen</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          {isEditing ? (
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="text-2xl font-bold"
              placeholder="Titel"
            />
          ) : (
            <CardTitle className="text-2xl">{task.title}</CardTitle>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status badges */}
          <div className="flex flex-wrap gap-2">
            {isEditing ? (
              <>
                <Select value={editStatus} onValueChange={(v) => setEditStatus(v as TaskStatus)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={editWebsite} onValueChange={(v) => setEditWebsite(v as WebsiteName)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(WEBSITE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={editPriority} onValueChange={(v) => setEditPriority(v as TaskPriority)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <>
                <Badge className={statusColors[task.status]}>
                  {TASK_STATUS_LABELS[task.status]}
                </Badge>
                <Badge variant="outline">
                  {WEBSITE_LABELS[task.website]}
                </Badge>
                <Badge className={priorityColors[task.priority]}>
                  {TASK_PRIORITY_LABELS[task.priority]}
                </Badge>
              </>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Omschrijving</h3>
            {isEditing ? (
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Omschrijving toevoegen..."
                rows={4}
              />
            ) : (
              <p className="text-sm">
                {task.description || <span className="text-muted-foreground italic">Geen omschrijving</span>}
              </p>
            )}
          </div>

          {/* Page Tags */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Pagina Tags</h3>
            <PageTagSelector
              selectedTags={isEditing ? editPageTags : (task.page_tags || [])}
              onChange={handlePageTagsChange}
              disabled={false}
            />
          </div>

          {/* Labels */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Labels</h3>
            <div className="flex flex-wrap gap-1">
              {(isEditing ? editLabels : task.labels)?.map((label) => (
                <Badge key={label} variant="secondary" className="gap-1">
                  {label}
                  {isEditing && (
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => removeLabel(label)}
                    />
                  )}
                </Badge>
              ))}
              {isEditing && (
                <div className="flex gap-1">
                  <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Nieuw label..."
                    className="h-6 w-32 text-xs"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                  />
                  <Button size="sm" className="h-6 px-2" onClick={addLabel}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {!isEditing && (!task.labels || task.labels.length === 0) && (
                <span className="text-sm text-muted-foreground italic">Geen labels</span>
              )}
            </div>
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Deadline
              </div>
              {isEditing ? (
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
              ) : (
                <p className="text-sm font-medium">
                  {task.due_date 
                    ? format(new Date(task.due_date), 'd MMMM yyyy', { locale: nl })
                    : <span className="text-muted-foreground italic">Niet ingesteld</span>
                  }
                </p>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                Toegewezen aan
              </div>
              {isEditing ? (
                <Select value={editAssignedTo} onValueChange={setEditAssignedTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer gebruiker" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niemand</SelectItem>
                    {profiles?.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.display_name || profile.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : assignee ? (
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs">
                      {assignee.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{assignee.display_name || assignee.email}</span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">Niet toegewezen</p>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                Aangemaakt
              </div>
              <p className="text-sm font-medium">
                {format(new Date(task.created_at), 'd MMMM yyyy', { locale: nl })}
                {creator && (
                  <span className="text-muted-foreground"> door {creator.display_name || creator.email}</span>
                )}
              </p>
            </div>

            {task.completed_at && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Voltooid
                </div>
                <p className="text-sm font-medium">
                  {format(new Date(task.completed_at), 'd MMMM yyyy', { locale: nl })}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
